import dayjs from 'dayjs';
import { checkSeekBuffer } from './video.util';
import { findVideoOffHole, isValidVideoCurrentTime } from './seek.util';
import { checkHLSEndTimestamp, seekStartPosition } from './helpers';
import { endDiscontinuity } from './discontinuity.util';
import { tracer } from './print';
/**
 * Seek forward to a new timestamp
 */
export function seekForwardTimestamp(
  context,
  video,
  startTimestamp,
  value,
  signal
) {
  tracer('seekForwardTimestamp', {
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    waitingTimestampCallerId: context.waitingTimestampCallerId,
  });
  try {
    // handle buffer seek if the buffer seek was available
    const bufferSeekAvailable = handleBufferSeekForward(
      context,
      video,
      startTimestamp,
      value
    );
    if (!bufferSeekAvailable) {
      // handle forward out buffer if the buffer seek was not available
      handleForwardOutBuffer(context, { startTimestamp }, signal);
    }
  } catch (error) {
    console.error('[ERROR][seekForwardTimestamp]', error);
  } finally {
    context.currentTimeIsChanging = false;
  }
}

/**
 * Handle seeking forward in buffer
 */
function handleBufferSeekForward(context, video, startTimestamp, value) {
  tracer('handleBufferSeekForward', {
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
  });

  // check if the buffer seek is available
  let seekBufferDetails = null;
  if (context.hlsPlaylist.length) {
    seekBufferDetails = checkSeekBuffer(
      video,
      startTimestamp,
      context.hlsPlaylist
    );
    tracer('handleBufferSeekForward/seekBufferDetails', {
      seekBufferDetails,
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    });
  }

  const timeInFragment = seekBufferDetails?.timeInFragment;
  const canSeekBuffer = seekBufferDetails?.canSeekBuffer;

  // check if the fragments are playing
  const fragmentsArePlaying = isPlayingStream(context);
  // when fragments are playing, the discontinuity is disabled
  if (fragmentsArePlaying) {
    // whether any hole in the buffer
    const videoOffHole = findVideoOffHole(context, startTimestamp);

    const isInGapTime =
      !!videoOffHole ||
      (context.backwardGapTime &&
        dayjs(startTimestamp).isBefore(
          context.backwardGapTime.programDateTime
        ));
    tracer('handleBufferSeekForward/isInGapTime', {
      isInGapTime,
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    });
    if (context.isDiscontinuity) {
      tracer('handleBufferSeekForward/isDiscontinuity', {
        isDiscontinuity: true,
        startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
      });
      context.handleForwardInDiscontinuity({
        startTimestamp,
        value,
        isInGapTime,
      });
      return true;
    }

    // handle requested timestamp fall in gap time caused by discontinuity fragment
    if (videoOffHole) {
      tracer('handleBufferSeekForward/videoOffHole', {
        videoOffHole,
        startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
      });
      console.warn(
        '[WARN] A video-off hole detected on forward ',
        { ...videoOffHole },
        context.hlsPlaylist
      );
      context.handleGoToTimeFallInGapTime({
        startTimestamp,
        videoOffHole,
      });
      return true;
    }

    // if no hole in the buffer rewind in the buffer
    if (isValidVideoCurrentTime(timeInFragment) && canSeekBuffer) {
      tracer('handleBufferSeekForward/isValidVideoCurrentTime', {
        isValidVideoCurrentTime: true,
        timeInFragment,
        startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
      });
      handleSeekInBuffer(context, {
        video,
        startTimestamp,
        videoTime: timeInFragment.videoCurrentTime,
        frag: timeInFragment.fragment,
      });
      return true;
    }

    // if fragments are not playing, then check if the discontinuity is enabled.
    // if discontinuity is enabled and the requested timestamp is available in the loaded fragment list, then end the discontinuity
    // if discontinuity is enabled and the requested timestamp is not available in the loaded fragment list, return false to handle forward out buffer
    // if discontinuity is not enabled, then return false to handle forward out buffer
  } else if (context.isDiscontinuity && timeInFragment) {
    const cpdSequence = context.getCpdSequence();
    tracer('handleBufferSeekForward/endDiscontinuity', {
      videoTime: timeInFragment.videoCurrentTime,
      timeInFragment,
      cpdSequence,
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    });
    if (
      timeInFragment.fragment.codecChanged ||
      (timeInFragment.fragment.cc >= cpdSequence && cpdSequence !== undefined)
    ) {
      return false;
    }
    endDiscontinuity(context, {
      videoTime: timeInFragment.videoCurrentTime,
      startTimestamp,
    });
    return true;
  }

  // the request time is not in loaded buffer and there is no hole detected in loaded buffer
  return false;
}

/**
 * Handle seeking forward out buffer
 */
async function handleForwardOutBuffer(context, { startTimestamp }, signal) {
  tracer('handleForwardOutBuffer', {
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
  });
  const endTimestamp = checkHLSEndTimestamp(
    startTimestamp,
    context.endTimestamp
  );

  const startTimestampOfBackBuffer = dayjs(startTimestamp)
    .subtract(seekStartPosition, 'seconds')
    .valueOf();

  // check if the fragment is in the backbuffer
  let bufferCheck = null;
  try {
    bufferCheck = await context.kvService.checkBackBufferFragmentExist(
      startTimestamp,
      seekStartPosition,
      signal
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    } else {
      console.error(`
          [ERROR] Unkown error on checkBackBufferFragmentExist: ${error}
          `);
    }
  }

  const { startFragExist, useBackBuffer } = bufferCheck;
  tracer('handleForwardOutBuffer/bufferCheck', {
    startFragExist,
    useBackBuffer,
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
  });

  //-------
  // Camera: NoFragmentFound - KVS Response: NoFragmentFound
  //-------
  // if no fragment found from KVS and the camera status is `NoFragmentFound` then just backward the timer
  //-------
  if (!startFragExist && context.isNoFragmentFound) {
    tracer('handleForwardOutBuffer/NoFragmentFound', {
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    });
    context.resetPlayerTimerWithNewTimestamp(startTimestamp);
    context.clearWaitingForNewTimestamp();
    return;
  }

  //-------
  // Camera: Discontinuity - KVS Response: NoFragmentFound
  //-------
  // if the camera status is `Discontinuity` already and the startTimestamp is not in the fragment list which mean the player is paused already and display `No Video` label
  // then just change the discontinuity timer to the new timestamp
  //-------
  if (context.isDiscontinuity && !startFragExist) {
    tracer('handleForwardOutBuffer/Discontinuity', {
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
    });
    context.resetPlayerTimerWithNewTimestamp(startTimestamp);
    context.clearWaitingForNewTimestamp();
    context.waitingTimestampCallerId = 'seek:discontinuity';
    return;
  }

  /**
   * request for new playback session when the requested timestamp is not in the fragment list
   */
  tracer('handleForwardOutBuffer/requestNewPlaybackSession', {
    startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
  });
  context.changeFragmentTime({
    direction: 'forward',
    startTimestamp: useBackBuffer ? startTimestampOfBackBuffer : startTimestamp,
    endTimestamp,
    live: false,
    pauseAfterLoad: !context.isPlaying,
    noLoading: true,
    callerPath: 'seek/!value',
    startPosition: useBackBuffer ? seekStartPosition : undefined,
    skipFragCheck: true,
  });
}

/**
 * Check if the player is playing the stream
 * @param {Vue} context
 * @returns
 */
function isPlayingStream(context) {
  return !(
    context.isNoFragmentFound ||
    context.isDiscontinuity ||
    context.isOutOfRetentionPeriod ||
    context.backwardGapTime
  );
}

/**
 * Handle seek in buffer
 */
function handleSeekInBuffer(
  context,
  { video, startTimestamp, videoTime, frag }
) {
  context.pausePlayerTimer();
  // check if it safe to rewind the player time to the requested startTimestamp if not it will request the new fragment from KVS
  if (!checkSafeSeekBuffer(context, { startTimestamp, frag })) {
    return;
  }
  // add the time elapsed to the player timer
  context.resetPlayerTimerWithNewTimestamp(startTimestamp);

  // set the new timestamp to the player video element
  video.currentTime = videoTime;

  // clear the waiting for new timestamp as the player time is changed
  context.$nextTick(() => {
    context.clearWaitingForNewTimestamp();
  });
}

/**
 * Check if it is safe to seek in buffer
 */
function checkSafeSeekBuffer(context, { startTimestamp }) {
  const signal = context.getFragmentCheckSignal();
  // when there is an unexpected pause while the player is playing and the player is not paused by the user
  if (!context.checkVideoPlaying() && context.isPlaying) {
    handleForwardOutBuffer(
      context,
      {
        startTimestamp,
      },
      signal
    );
    return false;
  }

  return true;
}
